import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { formatValue, FIELDS, tourTitle, statusColor, statusAbbrev, tourTypeColor, gratuityColor, gratuityPctColor } from "@/lib/fields";
import { Trash2, ChevronRight, Lock, Loader2, CalendarCheck } from "lucide-react";
import { usePrivacy } from "@/contexts/PrivacyContext";

const PAGE = 25;

const gratuityField = FIELDS.find((f) => f.key === "gratuity");

// Solid dot color per booking status (mirrors statusColor hues).
const statusDot = (s) =>
  ({
    Current: "bg-[#E0575B]",
    Complete: "bg-emerald-400",
    "Past (Update)": "bg-amber-400",
    "Future (Scheduled)": "bg-zinc-500",
    "Future (Request)": "bg-indigo-400",
    "Future (Possible)": "bg-indigo-400",
  }[s] || "bg-zinc-500");

// One short MM/DD/YY date; append +N if the tour spans more days.
const compactDate = (val) => {
  const arr = Array.isArray(val) ? val : val ? [val] : [];
  if (!arr.length) return "No date";
  const m = String(arr[0]).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  const short = m ? `${+m[1]}/${+m[2]}/${m[3].slice(-2)}` : String(arr[0]);
  return arr.length > 1 ? `${short} +${arr.length - 1}` : short;
};

export const GuestTable = ({ guests, onDelete, onConvert }) => {
  const navigate = useNavigate();
  const { financialPrivacy } = usePrivacy();
  const [visible, setVisible] = useState(() => Math.max(PAGE, Number(sessionStorage.getItem("toursVisible")) || PAGE));
  const sentinelRef = useRef(null);

  useEffect(() => { sessionStorage.setItem("toursVisible", String(visible)); }, [visible]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setVisible((v) => Math.min(v + PAGE, guests.length)); },
      { rootMargin: "500px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [guests.length, visible]);
  const lastId = typeof window !== "undefined" ? sessionStorage.getItem("lastTourId") : null;

  const openTour = (id) => {
    sessionStorage.setItem("lastTourId", id);
    navigate(`/guest/${id}`);
  };

  if (!guests.length) {
    return (
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-12 text-center" data-testid="empty-table">
        <p className="font-display text-lg text-zinc-300">No tours yet</p>
        <p className="mt-1 text-sm text-zinc-400">Add a tour or upload a tour sheet to build the database.</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-zinc-800/70 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/40" data-testid="guest-table">
      {guests.slice(0, visible).map((g, i) => {
        const isLast = g.id === lastId;
        const tip = g.gratuity;
        const hasTip = tip !== null && tip !== undefined && tip !== "";
        return (
          <motion.li
            key={g.id}
            data-testid={`guest-row-${g.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.25) }}
            onClick={() => openTour(g.id)}
            className={`group flex cursor-pointer items-center gap-3 border-l-2 px-3.5 py-3.5 transition-colors duration-150 active:bg-white/5 ${
              isLast ? "border-l-[#E0575B] bg-[#9B2226]/10" : "border-l-transparent hover:bg-white/5"
            }`}
          >
            <span
              className={`mt-1 h-2.5 w-2.5 shrink-0 self-start rounded-full ${statusDot(g.booking_status)}`}
              title={g.booking_status || ""}
              data-testid={`status-dot-${g.id}`}
            />

            <div className="min-w-0 flex-1">
              <p className="font-500 leading-snug text-zinc-100 line-clamp-2" data-testid={`tour-name-${g.id}`}>
                {tourTitle(g)}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-400">
                <span className="whitespace-nowrap">{compactDate(g.tour_date)}</span>
                <span className="text-zinc-500">·</span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-600 ${statusColor(g.booking_status)}`} data-testid={`status-abbrev-${g.id}`}>
                  {statusAbbrev(g.booking_status)}
                </span>
                {g.tour_type && (
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-600 ${tourTypeColor(g.tour_type)}`}>{g.tour_type}</span>
                )}
                {g.promo && (
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-600 bg-[#9B2226]/12 text-[#E0575B]" data-testid={`promo-tag-${g.id}`} title={`${g.promo} — 20% off`}>
                    {String(g.promo).replace(/ Promo$/i, "")} 20% off
                  </span>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 pl-1">
              {onConvert && (
                <button
                  data-testid={`convert-guest-${g.id}`}
                  onClick={(e) => { e.stopPropagation(); onConvert(g); }}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#9B2226]/50 bg-[#9B2226]/10 px-2.5 py-1 text-[11px] font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20"
                  title="Move this future request into your confirmed schedule"
                >
                  <CalendarCheck className="h-3.5 w-3.5" /> Convert to scheduled
                </button>
              )}
              <div className="text-right">
                {financialPrivacy ? (
                  <span className="inline-flex text-zinc-600" title="Financial privacy on" data-testid={`tip-hidden-${g.id}`}><Lock className="h-3.5 w-3.5" /></span>
                ) : hasTip ? (
                  <>
                    <p className={`whitespace-nowrap font-600 ${gratuityColor(g.tip_percent)}`}>{formatValue(gratuityField, tip)}</p>
                    {g.tip_percent !== null && g.tip_percent !== undefined && g.tip_percent !== "" && (
                      <p className={`text-[11px] ${gratuityPctColor(g.tip_percent)}`}>{g.tip_percent}%</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-zinc-600">—</p>
                )}
              </div>
              {onDelete && (
                <button
                  data-testid={`delete-guest-${g.id}`}
                  onClick={(e) => { e.stopPropagation(); onDelete(g); }}
                  className="hidden rounded-lg p-2 text-zinc-600 transition-colors duration-150 hover:bg-red-500/10 hover:text-red-400 sm:inline-flex"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <ChevronRight className="h-4 w-4 shrink-0 text-zinc-700 group-hover:text-zinc-400" />
            </div>
          </motion.li>
        );
      })}
      {visible < guests.length && (
        <li ref={sentinelRef} className="flex items-center justify-center gap-2 py-4 text-xs text-zinc-400" data-testid="tours-load-more">
          <Loader2 className="h-4 w-4 animate-spin text-[#E0575B]" /> Loading more… ({guests.length - visible} left)
        </li>
      )}
    </ul>
  );
};
